import React, { useContext, useState } from "react";
import Row from "@amzn/meridian/row";
import Table, { TableCell, TableRow } from "@amzn/meridian/table";
import Box from "@amzn/meridian/box";
import Text from "@amzn/meridian/text";
import FullScreen from "react-full-screen";
import { getDocumentURL } from "../../handler/ParentDataHandler";
import {
  ASSIGNABLE,
  DEPARTED,
  getOverviewStatusPriority,
  isRouteAtRisk,
  isRouteStalled,
  NOT_ASSIGNED,
  NOT_READY,
  PENDING_DEPART,
  PENDING_PICK,
  PENDING_STAGE,
  ROUTES_AT_RISK,
} from "../RouteStatusManager";
import Link from "@amzn/meridian/link";
import { FormattedMessage } from "react-intl";
import { OutboundFilterContext } from "../context/OutboundFilterContext";
import { OutboundConfigContext } from "../context/OutboundConfigContext";
import Column from "@amzn/meridian/column";
import { isARorSSD } from "../util/Util";
import { CapabilityContext } from "../../overview/context/CapabilityContext";
import { SSD } from "../util/Constants";
import ScrollableElementWrapper from "../../CommonModels/ScrollableElementWrapper";
import { useDeviceLayout } from "../../common/context/DeviceLayoutContext";

const VisualGridView = (props) => {
  const { spacingInset_shorthand2 } = useDeviceLayout();
  const [screenState, setScreenState] = useState(false);
  const { viewModelF } = useContext(OutboundFilterContext);
  const { outboundConfigModel } = useContext(OutboundConfigContext);
  const { capability } = useContext(CapabilityContext);

  const toggleScreen = () => {
    setScreenState(!screenState);
  };

  let routeList = props.routeList;
  routeList = routeList.sort((a, b) => a.criticalPullTime - b.criticalPullTime);
  const isNodeAROrSSD = isARorSSD(capability.data);
  const isDrsPickStageVisibilityEnabled =
    outboundConfigModel.stationConfig.drsPickStageVisibilityEnabled;
  const isAmxlStation = outboundConfigModel.stationConfig.amxl;
  const isSTAGWorkflowEnabled = outboundConfigModel.stationConfig.stagenabled;
  const routeWaveList = [];
  if (SSD !== capability.data) {
    routeList.forEach((route) => {
      const wave = routeWaveList.find((wave) => {
        return wave.name === route.routeSLA;
      });
      if (wave) {
        wave.routeList.push(route);
      } else {
        routeWaveList.push({
          name: route.routeSLA,
          routeList: [route],
        });
      }
    });
  } else {
    routeWaveList.push({
      name: "",
      routeList: routeList,
    });
  }

  // sort route list of each wave by status.
  routeWaveList.forEach((wave) => {
    wave.routeList.sort((a, b) => {
      const statusPriorityA = getOverviewStatusPriority(a.overviewStatus);
      const statusPriorityB = getOverviewStatusPriority(b.overviewStatus);
      if (statusPriorityA === statusPriorityB) {
        return 0;
      } else if (statusPriorityA < statusPriorityB) {
        return -1;
      } else {
        return 1;
      }
    });
  });

  const getRouteColor = (status) => {
    switch (status) {
      case ASSIGNABLE:
        return "#BBC0C1";
      case NOT_READY:
        return isNodeAROrSSD ? "#FAD000" : "#CB2FBF";
      case PENDING_PICK:
        return "#FAD000";
      case PENDING_STAGE:
        return "#FF714D";
      case PENDING_DEPART:
        return "#47C2EC";
      case DEPARTED:
        return "#98C91E";
      default:
        return "#BBC0C1";
    }
  };

  function naIfDefault(value) {
    return !value || value === "..." ? "NA" : value;
  }

  const onRouteClick = (route) => {
    getDocumentURL().then((result) => {
      const params = [
        "Overview",
        naIfDefault(route.routeId),
        naIfDefault(encodeURIComponent(route.rswsStatus)),
        naIfDefault(encodeURIComponent(route.stagingLocation)),
        naIfDefault(route.hazmatWeight),
        naIfDefault(route.picker),
        naIfDefault(route.routeCode),
        naIfDefault(route.stationCode),
      ];
      const url = new URL(result.replace("outboundAMZL", "pick"));
      const shareableLink = "/routeDetail/" + params.join("/");
      url.searchParams.set("shareableLink", shareableLink);
      if (isAmxlStation) {
        const amxlUrl = new URL(result.replace("outboundAMZL", "stowv2"));
        window.open(amxlUrl.href, "_parent");
      } else window.open(url.href, "_blank");
    });
  };

  const getDisplayProgress = (route) => {
    switch (route.overviewStatus) {
      case ASSIGNABLE:
      case PENDING_PICK:
        return getPickingProgress(route);
      case PENDING_STAGE:
        return (
          <span>
            Stage Progress: <b>{route.stageProgress}</b>
            <br />
          </span>
        );
      case PENDING_DEPART:
      case DEPARTED:
        return (
          <span>
            Depart Progress: <b>{route.departProgress}</b>
            <br />
          </span>
        );
      default:
        return "";
    }
  };

  const getPickingProgress = (route) => {
    if (outboundConfigModel.stationConfig.parallelPickEnabled) {
      let rows = [
        <span key={route.routeId + "count"}>
          Picklists: <b>{route.picklists.length}</b>
          <br />
        </span>,
        <span key={route.routeId + "progress"}>
          Pick Progress: <b>{route.pickProgress}</b>
          <br />
        </span>,
      ];
      rows = rows.concat(
        route.picklists.map((picklist) => {
          return picklist.status === NOT_ASSIGNED ? (
            <span key={picklist.picklistId}>
              {picklist.picklistCode}: <b>{NOT_ASSIGNED}</b>
              <br />
            </span>
          ) : (
            <span key={picklist.picklistId}>
              {picklist.picklistCode}: <b>{picklist.pickProgress}</b>
              <br />
            </span>
          );
        }),
      );
      return rows;
    } else {
      return (
        <span>
          Pick Progress: <b>{route.pickProgress}</b>
          <br />
        </span>
      );
    }
  };

  const boxWidth = 16;
  const boxHeight = 16;
  const partialIconSrc =
    "data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iVVRGLTgiPz4NCjxzdmcgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB2aWV3Qm94PSIwIDAgMjIgMjIiPjxjaXJjbGUgY3g9IjExIiBjeT0iLTEwNDEuMzYiIHI9IjgiIHRyYW5zZm9ybT0ibWF0cml4KDEgMCAwLTEgMC0xMDMwLjM2KSIgb3BhY2l0eT0iLjk4IiBmaWxsPSIjZGE0NDUzIi8+PHBhdGggZD0ibS0yNi4zMDkgMTguMDdjLTEuMTggMC0yLjEzNS45NjgtMi4xMzUgMi4xMjl2MTIuODJjMCAxLjE3Ni45NDggMi4xMjkgMi4xMzUgMi4xMjkgMS4xODMgMCAyLjEzNS0uOTY4IDIuMTM1LTIuMTI5di0xMi44MmMwLTEuMTc2LS45NDYtMi4xMjktMi4xMzUtMi4xMjl6bTAgMjEuMzQ4Yy0xLjE4IDAtMi4xMzUuOTU0LTIuMTM1IDIuMTM1IDAgMS4xOC45NTQgMi4xMzUgMi4xMzUgMi4xMzUgMS4xODEgMCAyLjEzNS0uOTU0IDIuMTM1LTIuMTM1IDAtMS4xOC0uOTUyLTIuMTM1LTIuMTM1LTIuMTM1eiIgdHJhbnNmb3JtPSJtYXRyaXgoLjMwMDU2IDAgMCAuMzAwNTYgMTguOTAyIDEuNzI4KSIgZmlsbD0iI2ZmZiIgc3Ryb2tlPSIjZmZmIi8+PC9zdmc+";
  const stalledIconSrc =
    "data:image/svg+xml,%3Csvg aria-hidden='true' focusable='false' xmlns='http://www.w3.org/2000/svg' width='16' height='16'%3E%3Cpath d='M15.89 14.55l-7-14a1 1 0 00-1.78 0l-7 14a1 1 0 000 1A1 1 0 001 16h14a1 1 0 00.89-1.45zM7 6a1 1 0 012 0v3a1 1 0 01-2 0zm1 8a1 1 0 111-1 1 1 0 01-1 1z' fill='currentColor'%3E%3C/path%3E%3C/svg%3E";

  const isRisky = (route) => {
    if (isSTAGWorkflowEnabled) {
      return false;
    }
    return isRouteAtRisk(
      route,
      viewModelF.sprPackageCount,
      viewModelF.sprTimeLimit,
    );
  };

  const getPartialIcon = () => {
    return (
      <img
        className="partial-icon"
        alt={"partial"}
        src={partialIconSrc}
        width="16px"
        height="16px"
      />
    );
  };

  const getStalledIcon = () => {
    return (
      <img
        className="stalled-icon"
        alt={"stalled"}
        src={stalledIconSrc}
        width="12px"
        height="12px"
      />
    );
  };

  const getSquareBox = (color, isPartial, height, width, isStalled) => {
    return (
      <Box
        minHeight={height}
        minWidth={width}
        maxHeight={height}
        maxWidth={width}
        backgroundColor={color}
      >
        {isPartial && getPartialIcon()}
        {isStalled && getStalledIcon()}
      </Box>
    );
  };

  const getSquareBoxWithBoundary = (color, isPartial, isStalled) => {
    return (
      <Box
        minHeight={boxHeight}
        minWidth={boxWidth}
        maxHeight={boxHeight}
        maxWidth={boxWidth}
        backgroundColor="#AD0A30"
        spacingInset="xxsmall"
      >
        {getSquareBox(color, isPartial, boxHeight - 4, boxWidth - 4, isStalled)}
      </Box>
    );
  };

  const renderRouteBox = (color, isAtRisk, isPartial, isStalled) => {
    return !isAtRisk || isNodeAROrSSD
      ? getSquareBox(color, isPartial, boxWidth, boxHeight, isStalled)
      : getSquareBoxWithBoundary(color, isPartial, isStalled);
  };

  const getDefaultLegend = (color, labelId, defaultLabel) => {
    return (
      <Row wrap={"none"} spacing={"small"}>
        {renderRouteBox(color)}
        <Text>
          <FormattedMessage id={labelId} defaultMessage={defaultLabel} />
        </Text>
      </Row>
    );
  };

  const getToolTipBody = (route) => {
    return (
      <Column className="tooltip-text" spacing={"none"}>
        <Row alignmentHorizontal={"center"}>
          <b>{route.routeCode}</b>
          <br />
        </Row>
        <Column spacing={"none"} alignmentHorizontal={"left"}>
          {getDisplayProgress(route)}
          {getStagingLocation(route)}
        </Column>
      </Column>
    );
  };

  const getStagingLocation = (route) => {
    return (
      <span style={{ textAlign: "left" }}>
        Location: {getToolTipTextFromList(route.stagingLocations)}
      </span>
    );
  };

  const getToolTipTextFromList = (list) => {
    if (Array.isArray(list) && list.length) {
      return list.map((element, index) => {
        return (
          <span key={element.displayLabel}>
            {" "}
            <b>
              {element.displayLabel}
              {index !== list.length - 1 ? "," : ""}
            </b>
            <br />
          </span>
        );
      });
    }
    return "...";
  };

  const renderVisualGridBoxes = (wave) => {
    return wave.routeList.map((route) => (
      <a
        key={route.routeId}
        onClick={() => {
          onRouteClick(route);
        }}
        style={{ textDecoration: "none", cursor: "pointer" }}
      >
        <Row className="html-tooltip" spacing={"none"} spacingInset={"none"}>
          {getToolTipBody(route)}
          {renderRouteBox(
            getRouteColor(route.overviewStatus),
            isRisky(route),
            route.isPartial,
            isRouteStalled(route, outboundConfigModel.stationConfig),
          )}
        </Row>
      </a>
    ));
  };

  return (
    <div>
      <Row
        widths={["grid-12"]}
        alignmentVertical="top"
        spacingInset={spacingInset_shorthand2}
        wrap={"down"}
      >
        <div>
          <FullScreen
            enabled={screenState}
            onChange={(enabled) => setScreenState(enabled)}
          >
            <ScrollableElementWrapper>
              <Table
                showStripes={true}
                headerColumns={1}
                stickyHeaderColumn={true}
                headerRows={1}
                spacing={"small"}
                fixHeaderRows={true}
                showDividers={true}
              >
                <TableRow>
                  {capability.data !== SSD ? (
                    <TableCell alignmentHorizontal={"right"} width="130px">
                      <FormattedMessage
                        id="outboundAMZL.stageByTime"
                        defaultMessage="Stage by time"
                      />
                    </TableCell>
                  ) : null}
                  {capability.data !== SSD ? (
                    <TableCell alignmentHorizontal={"right"}>
                      <FormattedMessage
                        id="outboundAMZL.routesInCaps"
                        defaultMessage="Routes"
                      />
                    </TableCell>
                  ) : null}
                  <TableCell alignmentHorizontal={"left"}>
                    <Row
                      widths={["grid-10", "grid-2"]}
                      spacing="xlarge"
                      alignmentHorizontal={"left"}
                    >
                      <Row spacing={"none small"}>
                        {isAmxlStation ||
                        isNodeAROrSSD ||
                        isDrsPickStageVisibilityEnabled
                          ? getDefaultLegend(
                              getRouteColor(NOT_READY),
                              "outboundAMZL.pendingStow",
                              "Pending Stow",
                            )
                          : null}
                        {getDefaultLegend(
                          getRouteColor(ASSIGNABLE),
                          "outboundAMZL.notAssigned",
                          "Not assigned",
                        )}
                        {!(
                          isNodeAROrSSD ||
                          isAmxlStation ||
                          isSTAGWorkflowEnabled
                        )
                          ? getDefaultLegend(
                              getRouteColor(PENDING_PICK),
                              "outboundAMZL.pendingPick",
                              PENDING_PICK,
                            )
                          : null}
                        {!isAmxlStation
                          ? getDefaultLegend(
                              getRouteColor(PENDING_STAGE),
                              "outboundAMZL.pendingStage",
                              PENDING_STAGE,
                            )
                          : null}
                        {getDefaultLegend(
                          getRouteColor(PENDING_DEPART),
                          "outboundAMZL.pendingDepart",
                          PENDING_DEPART,
                        )}
                        {getDefaultLegend(
                          getRouteColor(DEPARTED),
                          "outboundAMZL.departed",
                          DEPARTED,
                        )}
                        {!(isNodeAROrSSD || isSTAGWorkflowEnabled) ? (
                          <Row wrap={"none"} spacing={"small"}>
                            {renderRouteBox("primary", true)}
                            <Text>
                              <FormattedMessage
                                id="outboundAMZL.routeAtRisk"
                                defaultMessage={ROUTES_AT_RISK}
                              />
                            </Text>
                          </Row>
                        ) : null}
                        <Row wrap={"none"} spacing={"small"}>
                          <img
                            className="partial-icon"
                            alt={"partial"}
                            src={partialIconSrc}
                            width="16px"
                            height="16px"
                          />
                          <Text>
                            <FormattedMessage
                              id="outboundAMZL.partial"
                              defaultMessage={"Partial"}
                            />
                          </Text>
                        </Row>
                        {outboundConfigModel.stationConfig
                          .parallelPickEnabled ? (
                          <Row wrap={"none"} spacing={"small"}>
                            {getStalledIcon()}
                            <Text>
                              <FormattedMessage
                                id="outboundAMZL.stalled"
                                defaultMessage={"Stalled"}
                              />
                            </Text>
                          </Row>
                        ) : null}
                      </Row>
                      <Row alignmentHorizontal={"right"} spacing={"none"}>
                        <Link onClick={toggleScreen} type="secondary">
                          <FormattedMessage
                            id="outboundAMZL.toggleFullscreen"
                            defaultMessage="Toggle fullscreen"
                          />
                        </Link>
                      </Row>
                    </Row>
                  </TableCell>
                </TableRow>

                {/*GRID*/}
                {routeWaveList.map((wave) => (
                  <TableRow key={wave.name}>
                    {capability.data !== SSD || !isSTAGWorkflowEnabled ? (
                      <TableCell alignmentHorizontal="right">
                        {wave.name}
                      </TableCell>
                    ) : null}
                    {capability.data !== SSD || !isSTAGWorkflowEnabled ? (
                      <TableCell alignmentHorizontal="right">
                        {wave.routeList.length}
                      </TableCell>
                    ) : null}
                    <TableCell>
                      {capability.data === SSD || isSTAGWorkflowEnabled ? (
                        <Row
                          width="100%"
                          spacing="small"
                          wrap={"down"}
                          spacingInset={"none none large none"}
                        >
                          {renderVisualGridBoxes(wave)}
                        </Row>
                      ) : (
                        <Row width="100%" spacing="small" wrap={"down"}>
                          {renderVisualGridBoxes(wave)}
                        </Row>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </Table>
            </ScrollableElementWrapper>
          </FullScreen>
        </div>
      </Row>
    </div>
  );
};

export default VisualGridView;



// WEBPACK FOOTER //
// ./src/components/outboundAmzl/routegridview/VisualGridView.js